import React, { useState, useEffect, useRef } from "react";
import { 
  Box, 
  Typography, 
  Alert, 
  CircularProgress, 
  Snackbar,
  Paper,
  Container,
  Stack,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CalendarEntryApi, {
  CALENDAR_ENTRY_KINDS,
  CALENDAR_ENTRY_KIND_LABELS,
  CalendarEntryDto,
  CalendarEntryKind,
} from '../../api/CalendarEntryApi';
import { Link, useSearchParams } from 'react-router-dom';
import CalendarEditable from "../../components/CalendarEditable/CalendarEditable";
import CourtEventApi, {
  CourtEventDto,
  UpdateCourtEventDto,
  isAwaitingAppearanceEvent,
} from "../../api/CourtEventApi";
import { DateTime } from 'luxon';
import { View } from 'react-big-calendar';
import { staffTicketUrl } from '../../utils/appearanceAlertUrls';
import AddCourtDateDialog from '../../components/AddCourtDateDialog/AddCourtDateDialog';
import { COURT_EVENT_COLOR_KEY } from '../../utils/courtEventColor';
import './ManagerCalendarView.scss';

/**
 * A new non-case entry (Kelsi, Aug 20 2026).
 *
 * Defaults to time off with the whole-day box ticked, because that is what staff will reach for
 * most. `date`/`endDate` are the whole-day form; `start`/`end` carry a time when it is not.
 */
const emptyEntryForm = {
  title: '',
  desc: '',
  kind: 'time_off' as CalendarEntryKind,
  allDay: true,
  date: '',
  endDate: '',
  startTime: '09:00',
  endTime: '10:00',
};

const awaitingFetchWindow = () => {
  const now = DateTime.now();
  return {
    start: now.minus({ months: 6 }).startOf('day'),
    end: now.plus({ months: 1 }).endOf('day'),
  };
};

const ManagerCalendarView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const awaitingFilter = searchParams.get('appearanceOutcome') === 'awaiting';
  const [courtEvents, setCourtEvents] = useState<CourtEventDto[]>([]);
  const [calendarEntries, setCalendarEntries] = useState<CalendarEntryDto[]>([]);
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [entrySaving, setEntrySaving] = useState(false);
  const [entryForm, setEntryForm] = useState(emptyEntryForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [addCourtDateOpen, setAddCourtDateOpen] = useState(false);
  /*
   * A ref, not state: nothing renders from it. It exists only so a newly added court date can be
   * reloaded into the range actually on screen, and holding it in state would re-render the whole
   * calendar on every fetch for a value nobody reads.
   */
  const loadedRange = useRef<{ start: DateTime; end: DateTime } | null>(null);

  useEffect(() => {
    if (awaitingFilter) {
      const { start, end } = awaitingFetchWindow();
      loadCourtEvents(start, end);
    } else {
      loadCourtEvents();
    }
    loadCalendarEntries();
  }, [awaitingFilter]);

  const loadCourtEvents = async (fromDate?: DateTime, toDate?: DateTime) => {
    try {
      // setLoading(true);
      setError(null);

      // Get court events for the specified date range or default to this month
      const now = DateTime.now();
      const startDate = fromDate || now.startOf('month');
      const endDate = toDate || now.endOf('month');

      // Remembered so a new court date reloads into the range on screen, not the default month.
      loadedRange.current = { start: startDate, end: endDate };

      const events = await CourtEventApi.listCourtEvents({
        CourtEvent_Start_From: startDate.toMillis(),
        CourtEvent_Start_To: endDate.toMillis()
      });

      setCourtEvents(events);
    } catch (err) {
      console.log(err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load court events';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * The firm's non-case entries.
   *
   * Loaded unfiltered rather than for the visible window: the whole set is small (appointments and
   * time off for a handful of staff), and the court-event range juggling above exists because that
   * table has thousands of rows. A failure is surfaced but does not block the calendar — the
   * settings are the part somebody came here for.
   */
  const loadCalendarEntries = async () => {
    try {
      setCalendarEntries(await CalendarEntryApi.list());
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to load the calendar entries',
        'error',
      );
    }
  };

  const createEntry = async () => {
    setEntrySaving(true);
    try {
      // Whole-day entries run to the end of the last day, so a single day still has width on the
      // calendar and a range covers the final day rather than stopping at its midnight.
      const start = entryForm.allDay
        ? DateTime.fromISO(entryForm.date).startOf('day')
        : DateTime.fromISO(`${entryForm.date}T${entryForm.startTime}`);
      const end = entryForm.allDay
        ? DateTime.fromISO(entryForm.endDate || entryForm.date).endOf('day')
        : DateTime.fromISO(`${entryForm.date}T${entryForm.endTime}`);

      if (!start.isValid || !end.isValid) {
        showSnackbar('That date could not be read', 'error');
        return;
      }

      await CalendarEntryApi.create({
        CalendarEntry_Title: entryForm.title.trim(),
        CalendarEntry_Desc: entryForm.desc.trim() || null,
        CalendarEntry_Start: start.toMillis(),
        CalendarEntry_End: end.toMillis(),
        CalendarEntry_AllDay: entryForm.allDay,
        CalendarEntry_Kind: entryForm.kind,
      });

      setEntryForm(emptyEntryForm);
      setEntryFormOpen(false);
      await loadCalendarEntries();
      showSnackbar('Added to the calendar', 'success');
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to add the entry', 'error');
    } finally {
      setEntrySaving(false);
    }
  };

  const handleSaveEvent = async (event: CourtEventDto): Promise<boolean> => {
    try {
      const updateDto: UpdateCourtEventDto = {
        CourtEvent_guid: event.CourtEvent_guid,
        CourtEvent_Start: event.CourtEvent_Start,
        CourtEvent_End: event.CourtEvent_End,
        CourtEvent_Title: event.CourtEvent_Title,
        CourtEvent_Desc: event.CourtEvent_Desc,
        Ticket_guid: event.Ticket_guid
      };

      const updatedEvent = await CourtEventApi.updateCourtEvent(updateDto);
      
      // Update the local state with the updated event
      setCourtEvents(prevEvents => 
        prevEvents.map(e => 
          e.CourtEvent_guid === updatedEvent.CourtEvent_guid ? updatedEvent : e
        )
      );

      showSnackbar('Court event updated successfully', 'success');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update court event';
      showSnackbar(errorMessage, 'error');
      return false;
    }
  };

  const handleDeleteEvent = async (event: CourtEventDto): Promise<boolean> => {
    try {
      await CourtEventApi.deleteCourtEvent(event.CourtEvent_guid);
      
      // Remove the deleted event from local state
      setCourtEvents(prevEvents => 
        prevEvents.filter(e => e.CourtEvent_guid !== event.CourtEvent_guid)
      );

      showSnackbar('Court event deleted successfully', 'success');
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete court event';
      showSnackbar(errorMessage, 'error');
      return false;
    }
  };

  const handleRangeChange = (date: Date, view: View) => {
    if (awaitingFilter) {
      // Keep the wide window so changing month does not drop awaiting rows from other months.
      const { start, end } = awaitingFetchWindow();
      loadCourtEvents(start, end);
      return;
    }

    const luxonDate = DateTime.fromJSDate(date);
    let startDate: DateTime;
    let endDate: DateTime;

    // Calculate date range based on the calendar view
    switch (view) {
      case 'month':
        // Load entire month plus a few days buffer
        startDate = luxonDate.startOf('month').minus({ days: 7 });
        endDate = luxonDate.endOf('month').plus({ days: 7 });
        break;
      case 'week':
        // Load the week plus buffer
        startDate = luxonDate.startOf('week').minus({ days: 3 });
        endDate = luxonDate.endOf('week').plus({ days: 3 });
        break;
      case 'day':
        // Load the day plus buffer
        startDate = luxonDate.minus({ days: 1 });
        endDate = luxonDate.plus({ days: 1 });
        break;
      case 'agenda':
        // Load a month for agenda view
        startDate = luxonDate.minus({ days: 15 });
        endDate = luxonDate.plus({ days: 45 });
        break;
      default:
        // Default to month view
        startDate = luxonDate.startOf('month').minus({ days: 7 });
        endDate = luxonDate.endOf('month').plus({ days: 7 });
    }

    // Load events for the new date range
    loadCourtEvents(startDate, endDate);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const visibleEvents = awaitingFilter
    ? courtEvents.filter((event) => isAwaitingAppearanceEvent(event))
    : courtEvents;

  if (loading) {
    return (
      <>
        <Container maxWidth="xl" className="manager-calendar-view">
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (error && courtEvents.length === 0) {
    return (
      <>
        <Container maxWidth="xl" className="manager-calendar-view">
          <Box sx={{ mt: 3 }}>
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <Container maxWidth="xl" className="manager-calendar-view">
      <Box sx={{ mt: 3, mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Court Events Calendar
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {awaitingFilter
            ? 'Settings that required the client to attend, have passed, and still have no appeared / did not appear answer.'
            : 'Manage court events for the next 30 days. Click on events to view details, make changes, or handle scheduling conflicts.'}
        </Typography>
      </Box>

      {awaitingFilter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing only unanswered required appearances from the last six months.
          Other hearings are hidden until this filter is cleared.
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some events may not have loaded properly: {error}
        </Alert>
      )}

      {/*
        Non-case entries: Lloyd's appointments, court dates with no client, and staff time off
        (Kelsi, Aug 20 2026). Hidden behind the awaiting filter, which is a view of one specific
        problem and would only be muddied by them.
      */}
      {!awaitingFilter && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ mb: entryFormOpen ? 2 : 0 }}>
            {/*
              A court date typed in by hand (19 Sep meeting). Not everything arrives as a PDF
              notice: county courts email a docket listing dozens of defendants, and there is
              nothing in that to upload. Without this, those dates never reach the calendar.
            */}
            <Button variant="contained" onClick={() => setAddCourtDateOpen(true)}>
              Add a court date
            </Button>
            <Button variant="outlined" onClick={() => setEntryFormOpen(!entryFormOpen)}>
              {entryFormOpen ? 'Cancel' : 'Add a non-case entry'}
            </Button>
          </Stack>

          {entryFormOpen && (
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  select
                  label="Kind"
                  size="small"
                  sx={{ minWidth: 190 }}
                  value={entryForm.kind}
                  onChange={(e) =>
                    setEntryForm({ ...entryForm, kind: e.target.value as CalendarEntryKind })
                  }
                >
                  {CALENDAR_ENTRY_KINDS.map((kind) => (
                    <MenuItem key={kind} value={kind}>
                      {CALENDAR_ENTRY_KIND_LABELS[kind]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="What is it"
                  size="small"
                  required
                  sx={{ minWidth: 240 }}
                  value={entryForm.title}
                  onChange={(e) => setEntryForm({ ...entryForm, title: e.target.value })}
                />
                <TextField
                  label={entryForm.allDay ? 'First day' : 'Date'}
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={entryForm.date}
                  onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                />
                {entryForm.allDay ? (
                  <TextField
                    label="Last day"
                    type="date"
                    size="small"
                    // Blank means a single day, which is the common case for an appointment and
                    // saves typing the same date twice.
                    helperText="Leave blank for one day"
                    InputLabelProps={{ shrink: true }}
                    value={entryForm.endDate}
                    onChange={(e) => setEntryForm({ ...entryForm, endDate: e.target.value })}
                  />
                ) : (
                  <>
                    <TextField
                      label="From"
                      type="time"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={entryForm.startTime}
                      onChange={(e) => setEntryForm({ ...entryForm, startTime: e.target.value })}
                    />
                    <TextField
                      label="To"
                      type="time"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={entryForm.endTime}
                      onChange={(e) => setEntryForm({ ...entryForm, endTime: e.target.value })}
                    />
                  </>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={entryForm.allDay}
                      onChange={(e) => setEntryForm({ ...entryForm, allDay: e.target.checked })}
                    />
                  }
                  label="Whole days"
                />
                <Button
                  variant="contained"
                  disabled={entrySaving || !entryForm.title.trim() || !entryForm.date}
                  onClick={createEntry}
                >
                  {entrySaving ? 'Adding…' : 'Add'}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Everyone can see this. Only you can change or remove it.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      <AddCourtDateDialog
        open={addCourtDateOpen}
        onClose={() => setAddCourtDateOpen(false)}
        onCreated={() => {
          // Back into the range on screen, not the default month — see loadedRange.
          loadCourtEvents(loadedRange.current?.start, loadedRange.current?.end);
          setSnackbarSeverity('success');
          setSnackbarMessage('Court date added');
          setSnackbarOpen(true);
        }}
      />

      {/*
        What the colours mean. Ten of them now carry information, and a scheme nobody can read is
        the same as no scheme — "how does it decide what gets what color?" was Kelsi's question in
        the 19 Sep meeting, about the old one.
      */}
      <Stack direction="row" flexWrap="wrap" sx={{ gap: 1.5, mb: 1.5, px: 0.5 }}>
        {COURT_EVENT_COLOR_KEY.map(({ label, color }) => (
          <Stack key={label} direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: color }} />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Paper elevation={1} sx={{ p: 2 }}>
        <CalendarEditable
          courtEvents={visibleEvents}
          calendarEntries={awaitingFilter ? [] : calendarEntries}
          onEntryChanged={loadCalendarEntries}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          onRangeChange={handleRangeChange}
        />
      </Paper>

      {awaitingFilter && (
        <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Unanswered required appearances ({visibleEvents.length})
          </Typography>
          {visibleEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No required appearances in the last six months are still unanswered.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleEvents.map((event) => (
                <Box
                  key={event.CourtEvent_guid}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}
                >
                  <Typography variant="body2">
                    {DateTime.fromMillis(Number(event.CourtEvent_Start)).toFormat('yyyy-LL-dd')}
                    {' — '}
                    {event.CourtEvent_Title || 'Court event'}
                  </Typography>
                  {event.Ticket_guid && (
                    <Button
                      component={Link}
                      to={staffTicketUrl(event.Ticket_guid, event.CourtEvent_guid)}
                      size="small"
                    >
                      Open case
                    </Button>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
    </>
  );
};

export default ManagerCalendarView;
